import { faChevronRight, faLockOpen, faPersonChalkboard, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonType } from "components/Button/Button";
import { DataFetcher } from "components/DataFetcher";
import MediaType from "components/MediaType/MediaType";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModuleStateType, TraversalRuleType } from "utils/constants";
import ProgressIcon from "./ProgressIcon";
import "./SubModuleAccordion.scss";
import ShareModal from "components/ShareModal/ShareModal";
import { useModal } from "components/Modal/context/ModalContext";
import { ModalButtons } from "components/Modal/ModalButtons";

const SubModuleAccordion = ({
	expanded,
	courseId,
	moduleId,
	submoduleCount,
	guruUrl,
	webcast,
	contactNumber,
	state,
	handleOpenLockModal,
	handleOpenCourseTraversalModal,
	onExpand,
	headerRef,
	courseName,
	supressExpand,
	previewMode = false,
	previewLockedLearningJourneys = false,
	previewSubmodules = [],
	draftId = null
}) => {
	const [toggle, setToggle] = useState(expanded);
	const [module, setModule] = useState({});
	const [submodules, setSubmodules] = useState([]);
	const [height, setHeight] = useState("0");
	const panelRef = useRef(null);
	const buttonRef = useRef(null);
	const navigate = useNavigate();
	const [fetchData, setFetchData] = useState({});
	const { openModal } = useModal();

	useEffect(() => {
		setToggle(expanded);
	}, [expanded]);

	useEffect(() => {
		if (!previewMode) {
			return;
		}

		setSubmodules((previewSubmodules ?? []).slice());
	}, [previewMode, previewSubmodules]);

	const togglePanel = () => {
		onExpand();
	};

	const buildSubmoduleLink = (submoduleId) => {
		if (previewMode) {
			const base = `courseId-${courseId}-moduleId-${moduleId}-submoduleId-${submoduleId}`;
			const hash = draftId ? `${base}-draftId-${draftId}` : base;
			return `/player-preview#${hash}`;
		}
		return `/player#courseId-${courseId}-moduleId-${moduleId}-submoduleId-${submoduleId}`;
	};

	const handleOpenShareModal = ({ shareLink, contentName }) => {
		if (previewMode) {
			openModal("Preview Mode", "Share unavailable while previewing a course.", ModalButtons.Ok);
		}
		else {
			openModal("Share", <ShareModal shareLink={shareLink} contentName={contentName} />, ModalButtons.None);
		}
	};

	useEffect(() => {
		if (!panelRef.current) {
			return;
		}

		if (!toggle) {
			setHeight("0");
			return;
		}

		if (previewMode) {
			setHeight(`${panelRef.current.scrollHeight}px`);

			if (headerRef?.current && !supressExpand) {
				const topPosition = headerRef.current.getBoundingClientRect().top + window.scrollY - 30;
				window.scrollTo({ top: topPosition, behavior: "smooth" });
			}

			return;
		}

		if (submodules.length === 0) {
			setFetchData({ url: `/api/Submodules/ByModuleId/${moduleId}` });
			return;
		}

		setHeight(`${panelRef.current.scrollHeight}px`);

		if (headerRef?.current && !supressExpand) {
			const topPosition = headerRef.current.getBoundingClientRect().top + window.scrollY - 30;
			window.scrollTo({ top: topPosition, behavior: "smooth" });
		}
	}, [toggle, submodules, moduleId, previewMode, headerRef, supressExpand]);

	const handleSetData = useCallback((data) => {
		if (previewMode) {
			return;
		}

		if (submodules.length === 0) {
			setSubmodules(() => {
				data.SubContent.sort((a, b) => {
					return a.Position - b.Position;
				});

				return data.SubContent;
			});

			setModule(data);

			if (toggle && panelRef.current) {
				setHeight(`${panelRef.current.scrollHeight}px`);
				buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previewMode, submodules.length, toggle]);

	const handleClick = (submodule) => {
		navigate(buildSubmoduleLink(submodule.Id));
	};

	const getExtraGuruText = () => {
		const message = !webcast
			? "Need expert help with a part of your phone system? Book a free 1 to 1 guru session with us."
			: `Need expert help with this topic? Book a free 1 to 1 Guru session or call us on ${contactNumber} to schedule a webcast.`;

		return (
			<div className="extra-text">
				<h3>Still searching for what you need?</h3>
				<p>{message}</p>
			</div>
		);
	};

	const effectiveSubmoduleCount = previewMode ? (previewSubmodules?.length ?? 0) : submoduleCount;

	if (effectiveSubmoduleCount === 0) {
		return null;
	}

	return (
		<div className="list-accordion">
			<DataFetcher fetchData={previewMode ? {} : fetchData} setData={handleSetData}>
				<button ref={buttonRef} className="accordionToggle" onClick={togglePanel}>
					<span className="button-text">{toggle ? "Hide" : "See"} {effectiveSubmoduleCount} Submodules</span>
					<span className="button-arrow">
						<span className={`button-arrow-icon ${toggle ? "toggled" : ""}`}>
							<FontAwesomeIcon icon={faChevronRight} />
						</span>
					</span>
				</button>

				<div
					ref={panelRef}
					className="list-accordion-panel"
					style={{ height: height, transition: "height 0.8s ease" }}
				>
					<h3>Submodules</h3>

					{submodules.map((submodule, index) => {
						const progressValue = submodule?.Progress?.Value ?? 0;
						const progressSuccess = submodule?.Progress?.Success ?? true;
						const contentType = submodule?.ContentType ?? [];
						const hasContent = Array.isArray(contentType) ? contentType.length : Boolean(contentType?.length);

						return (
							<div className="submodule" key={index}>
								<ProgressIcon
									state={
										previewMode
											? (
												previewLockedLearningJourneys
													? submodule.State
													: ModuleStateType.Enabled
											)
											: (
												submodule.State !== ModuleStateType.CourseTraversal
													? state
													: submodule.State
											)
									}
									ruleType={module.TraversalRule}
									hasContent={hasContent}
									className="submodule-progress"
									progressData={{
										hasProgress: previewMode && !previewLockedLearningJourneys ? false : submodule.HasProgress,
										value: previewMode && !previewLockedLearningJourneys ? 0 : progressValue,
										asPercentage: false,
										success: previewMode && !previewLockedLearningJourneys ? true : progressSuccess
									}}
									handleClick={() => handleClick(submodule)}
									handleOpenLockModal={handleOpenLockModal}
									handleOpenCourseTraversalModal={handleOpenCourseTraversalModal}
									previewMode={previewMode}
								/>

								<div className="submodule-details">
									<div className="submodule-details-share">
										<Button
											onClick={() =>
												handleOpenShareModal({
													shareLink: buildSubmoduleLink(submodule.Id),
													contentName: `${courseName} > ${module.Name} > ${submodule.Name}`
												})
											}
											type={ButtonType.Tertiary}
											text="Share"
											icon={faShareNodes}
										/>
									</div>

									<div className="submodule-details-text">
										<h4>{submodule.Name}</h4>
										<MediaType type={contentType} />
									</div>

									{!previewMode && submodule.State === ModuleStateType.CourseTraversal && (
										<div className="submodule-details-llj">
											{module.TraversalRule === TraversalRuleType.EnableAfterPrevious ? (
												<p>Complete the previous submodule to unlock this submodule</p>
											) : (
												<p>Complete the first submodule to unlock this submodule</p>
											)}
										</div>
									)}
								</div>
							</div>
						);
					})}

					{guruUrl?.length > 0 && (
						<div className="extra">
							<div className="extra-icon">
								<FontAwesomeIcon icon={faPersonChalkboard} className="fa-2x" />
							</div>

							{getExtraGuruText()}

							<div className="extra-link">
								<Button type={ButtonType.Primary} text="Book Now" to={guruUrl} newTab />
							</div>
						</div>
					)}

					{!previewMode && state === ModuleStateType.DisabledUpsell && (
						<div className="extra">
							<div className="extra-icon">
								<FontAwesomeIcon icon={faLockOpen} className="fa-2x" />
							</div>

							<div className="extra-text">
								<h3 className="extra-text-alone">Want to Unlock this Content?</h3>
							</div>

							<div className="extra-link">
								<Button type={ButtonType.Secondary} onClick={handleOpenLockModal} text="Unlock Now" />
							</div>
						</div>
					)}
				</div>
			</DataFetcher>
		</div>
	);
};

export default SubModuleAccordion;